import { useAtom } from "jotai";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AddressFormFields } from "~/components/ui/address-form-fields";
import { AddressView } from "~/components/ui/address-view";
import { Button } from "~/components/ui/button";
import { Form } from "~/components/ui/form";
import { useUpdateSellerMutation } from "~/graphql/graphql.gen";
import { brandState } from "~/stores/brand";
import type { Address } from "~/utils/address";
import { formatCountryName } from "~/utils/address";

export type OnboardingStepReturnAddressProps = {
  goToNextStep: () => void;
};

export type OnboardingStepReturnAddressForm = Address;

export const OnboardingStepReturnAddress: React.FC<
  OnboardingStepReturnAddressProps
> = ({ goToNextStep }: OnboardingStepReturnAddressProps) => {
  const { i18n, t } = useTranslation();
  const [brand, setBrand] = useAtom(brandState);

  const { mutateAsync: updateSellerMutation } = useUpdateSellerMutation();

  const form = useForm<OnboardingStepReturnAddressForm>({
    mode: "onTouched",
    defaultValues: {
      name: brand?.return_address?.name ?? "",
      street: brand?.return_address?.street ?? "",
      street2: brand?.return_address?.street2 ?? "",
      bp: brand?.return_address?.bp ?? "",
      province: brand?.return_address?.province ?? "",
      city: brand?.return_address?.city ?? "",
      country:
        brand?.return_address?.country ??
        formatCountryName(
          brand?.shipping_country?.toUpperCase() ?? "FR",
          i18n.language.replace("_", "-"),
        ),
      countryCode:
        brand?.return_address?.countryCode ?? brand?.shipping_country ?? "FR",
    },
  });
  const [isFilledAddress, setIsFilledAddress] = React.useState(
    !!brand?.return_address,
  );

  const onSubmit = (data: OnboardingStepReturnAddressForm) => {
    if (!brand) return;
    setBrand({
      ...brand,
      return_address: data,
    });
    updateSellerMutation({
      updateSeller: { return_address: data },
    });
    setIsFilledAddress(true);
  };

  const onEditAddress = () => {
    setIsFilledAddress(false);
  };

  return (
    <section className="flex w-full flex-col gap-10">
      {isFilledAddress ? (
        <>
          <div className="mt-8 sm:mt-20">
            <h2 className="text-2xl font-bold sm:text-3.5xl">
              {t("onboarding.return_address.confirm_title")}
            </h2>
          </div>
          <div className="flex flex-col items-start">
            <div className="min-w-[18.375rem] rounded-lg border border-gray-500 p-6">
              {brand?.return_address ? (
                <dl>
                  <AddressView
                    label={t("address.label")}
                    address={brand.return_address}
                    locale={i18n.language === "fr" ? "FR" : "EN"}
                  />
                </dl>
              ) : null}
              <button
                type="button"
                className="mt-10 text-sm font-bold text-green-900 focus-visible:ring-2 focus-visible:ring-gray-900/30"
                onClick={onEditAddress}
              >
                {t("edit")}
              </button>
            </div>
          </div>

          <Button
            type="button"
            size="lg"
            onClick={goToNextStep}
            className="self-start"
          >
            {t("confirm")}
          </Button>
        </>
      ) : (
        <>
          <div className="mt-8 flex flex-col gap-2 sm:mt-20">
            <h2 className="text-2xl font-bold sm:text-3.5xl">
              {t("onboarding.return_address.title")}
            </h2>
            <p className="text-sm leading-5.5 text-gray-700">
              {t("onboarding.return_address.subtitle")}
            </p>
          </div>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-10"
            >
              <AddressFormFields
                locale={i18n.language}
                showCompany={true}
                showName={false}
                showPhone={false}
                autoFocus
                translations={{
                  suggestions: t("suggestions"),
                  fieldsNameValidationErrorsRequired: t(
                    "address.fields.name.validation_errors.required",
                  ),
                  fieldsNameValidationErrorsMaxLength: t(
                    "address.fields.name.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsFirstnameValidationErrorsRequired: t(
                    "address.fields.firstname.validation_errors.required",
                  ),
                  fieldsFirstnameValidationErrorsMaxLength: t(
                    "address.fields.firstname.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsLastnameValidationErrorsRequired: t(
                    "address.fields.lastname.validation_errors.required",
                  ),
                  fieldsLastnameValidationErrorsMaxLength: t(
                    "address.fields.lastname.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsPhoneValidationErrorsRequired: t(
                    "address.fields.phone.validation_errors.required",
                  ),
                  fieldsPhoneValidationErrorsMaxLength: t(
                    "address.fields.phone.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsStreetValidationErrorsRequired: t(
                    "address.fields.street.validation_errors.required",
                  ),
                  fieldsStreetValidationErrorsMaxLength: t(
                    "address.fields.street.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsStreet2ValidationErrorsMaxLength: t(
                    "address.fields.street2.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsBpValidationErrorsRequired: t(
                    "address.fields.bp.validation_errors.required",
                  ),
                  fieldsBpValidationErrorsMaxLength: t(
                    "address.fields.bp.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsCityValidationErrorsRequired: t(
                    "address.fields.city.validation_errors.required",
                  ),
                  fieldsCityValidationErrorsMaxLength: t(
                    "address.fields.city.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsProvinceValidationErrorsRequired: t(
                    "address.fields.province.validation_errors.required",
                  ),
                  fieldsProvinceValidationErrorsMaxLength: t(
                    "address.fields.province.validation_errors.maxLength",
                    {
                      maxLength: "200",
                    },
                  ),
                  fieldsCountryValidationErrorsRequired: t(
                    "address.fields.country.validation_errors.required",
                  ),
                  fieldsNameLabel: t("address.fields.name.label_return"),
                  fieldsNamePlaceholder: t("address.fields.name.placeholder"),
                  fieldsFirstnameLabel: t("address.fields.firstname.label"),
                  fieldsFirstnamePlaceholder: t(
                    "address.fields.firstname.placeholder",
                  ),
                  fieldsLastnameLabel: t("address.fields.lastname.label"),
                  fieldsLastnamePlaceholder: t(
                    "address.fields.lastname.placeholder",
                  ),
                  fieldsPhoneLabel: t("address.fields.phone.label"),
                  fieldsPhonePlaceholder: t("address.fields.phone.placeholder"),
                  fieldsStreetLabel: t("address.fields.street.label"),
                  fieldsStreetPlaceholder: t(
                    "address.fields.street.placeholder",
                  ),
                  fieldsStreet2Label: t("address.fields.street2.label"),
                  fieldsStreet2Placeholder: t(
                    "address.fields.street2.placeholder",
                  ),
                  fieldsBpLabel: t("address.fields.bp.label"),
                  fieldsBpPlaceholder: "",
                  fieldsCityLabel: t("address.fields.city.label"),
                  fieldsCityPlaceholder: "",
                  fieldsProvinceLabel: t("address.fields.province.label"),
                  fieldsProvincePlaceholder: "",
                  fieldsProvinceEmptyField: t(
                    "address.fields.province.empty_field",
                  ),
                  fieldsCountryLabel: t("address.fields.country.label"),
                  fieldsCountryPlaceholder: t(
                    "address.fields.country.placeholder",
                  ),
                  fieldsCountryPlaceholderSearch: t(
                    "address.fields.country.placeholder_search",
                  ),
                  fieldsCountryEmptyField: t(
                    "address.fields.country.empty_field",
                  ),
                  fieldsCountryNoResults: t(
                    "address.fields.country.no_results",
                  ),
                }}
              />

              <Button type="submit" size="lg" className="self-start">
                {t("continue")}
              </Button>
            </form>
          </Form>
        </>
      )}
    </section>
  );
};

OnboardingStepReturnAddress.displayName = "OnboardingStepReturnAddress";
